import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/* ─────────────  I18N  ───────────── */
export const PDF_LANGS = [
  { code: "ru", label: "Русский" },
  { code: "en", label: "English" },
  { code: "zh", label: "中文" },
];

export const PDF_CURRENCIES = [
  { code: "EUR", symbol: "€",  rate: 1.0   },
  { code: "USD", symbol: "$",  rate: 1.08  },
  { code: "GBP", symbol: "£",  rate: 0.85  },
  { code: "CHF", symbol: "₣",  rate: 0.94  },
  { code: "JPY", symbol: "¥",  rate: 162.5 },
  { code: "CNY", symbol: "¥",  rate: 7.85  },
  { code: "AED", symbol: "د.إ",rate: 3.97  },
  { code: "KZT", symbol: "₸",  rate: 550.0 },
  { code: "RUB", symbol: "₽",  rate: 97.0  },
];

// Overrides PDF_CURRENCIES rates in-place with live ECB data on demand.
let _fxFetched = false;
export async function refreshFxRates() {
  if (_fxFetched) return PDF_CURRENCIES;
  try {
    const mod = await import("../api");
    const r = await mod.default.get("/fx/rates");
    const live = r.data?.rates || {};
    PDF_CURRENCIES.forEach((c) => {
      if (live[c.code] != null) c.rate = Number(live[c.code]);
    });
    _fxFetched = true;
  } catch (e) { /* keep static fallback */ }
  return PDF_CURRENCIES;
}

const T = {
  titles: {
    "SWIFT-B2B":  { ru: "SWIFT B2B · КВИТАНЦИЯ",     en: "SWIFT B2B RECEIPT",     zh: "SWIFT B2B · 收据" },
    "WITHDRAW":   { ru: "КВИТАНЦИЯ О ВЫВОДЕ",        en: "WITHDRAWAL RECEIPT",    zh: "提现收据" },
    "ACQUIRING":  { ru: "СЧЁТ НА ОПЛАТУ (QR)",       en: "QR PAYMENT INVOICE",    zh: "二维码付款发票" },
  },
  subtitle:     { ru: "Гибридная глобальная экономика · TradFi + DeFi", en: "Hybrid Global Economy · TradFi + DeFi", zh: "混合全球经济 · TradFi + DeFi" },
  details:      { ru: "Детали операции",  en: "Transaction Details", zh: "交易明细" },
  badge_ok:     { ru: "✓ ЗАВЕРШЕНО НА БЛОКЧЕЙНЕ",    en: "✓ SETTLED ON-CHAIN",    zh: "✓ 链上已结算" },
  badge_wait:   { ru: "⏱ ОЖИДАЕТ ОПЛАТЫ",            en: "⏱ AWAITING PAYMENT",    zh: "⏱ 等待付款" },
  qr_hint:      { ru: "Отсканируйте QR в приложении Turbo Global для оплаты", en: "Scan this QR in Turbo Global app to pay", zh: "请在 Turbo Global 应用中扫描此二维码以付款" },
  footer_line1: { ru: "Обработано через Turbo Global blockchain rails · ≈8 секунд · комиссия 0.1–0.5%", en: "Processed via Turbo Global blockchain rails · ~8 sec · 0.1-0.5% fee", zh: "通过 Turbo Global 区块链处理 · 约 8 秒 · 手续费 0.1–0.5%" },
  footer_line2: { ru: "Автоматически сгенерированная квитанция. Подпись не требуется.", en: "Auto-generated receipt. No signature required.", zh: "自动生成的收据，无需签名。" },
  ref:          { ru: "Номер",    en: "Ref",       zh: "编号" },
  date:         { ru: "Дата",     en: "Date",      zh: "日期" },
};

/* English row-label → localized */
const ROW_LABELS = {
  // SWIFT
  "Beneficiary":      { ru: "Получатель",      en: "Beneficiary",     zh: "收款人" },
  "IBAN / Account":   { ru: "IBAN / счёт",     en: "IBAN / Account",  zh: "IBAN / 账号" },
  "SWIFT / BIC":      { ru: "SWIFT / BIC",     en: "SWIFT / BIC",     zh: "SWIFT / BIC" },
  "Amount":           { ru: "Сумма",           en: "Amount",          zh: "金额" },
  "Fee (0.3%)":       { ru: "Комиссия (0.3%)", en: "Fee (0.3%)",      zh: "手续费 (0.3%)" },
  "Purpose":          { ru: "Назначение",      en: "Purpose",         zh: "用途" },
  "ETA":              { ru: "Зачисление",      en: "ETA",             zh: "到账时间" },
  "Sent at":          { ru: "Отправлено",      en: "Sent at",         zh: "发送时间" },
  // Withdraw
  "Amount (TGC)":     { ru: "Сумма (TGC)",     en: "Amount (TGC)",    zh: "金额 (TGC)" },
  "Amount (EUR)":     { ru: "Сумма",           en: "Amount",          zh: "金额" },
  "Method":           { ru: "Метод",           en: "Method",          zh: "方式" },
  "Destination":      { ru: "Получатель",      en: "Destination",     zh: "目标账户" },
  "Initiated at":     { ru: "Инициировано",    en: "Initiated at",    zh: "发起时间" },
  // Acquiring
  "Invoice ID":       { ru: "Номер счёта",     en: "Invoice ID",      zh: "发票编号" },
  "Currency":         { ru: "Валюта",          en: "Currency",        zh: "币种" },
  "Terminal / POS":   { ru: "Терминал / POS",  en: "Terminal / POS",  zh: "终端 / POS" },
  "Fee (merchant)":   { ru: "Комиссия",        en: "Fee (merchant)",  zh: "手续费" },
  "Created at":       { ru: "Создано",         en: "Created at",      zh: "创建时间" },
  "Status":           { ru: "Статус",          en: "Status",          zh: "状态" },
};

const tr = (dict, lang) => dict[lang] || dict.en;
const trLabel = (label, lang) => (ROW_LABELS[label] ? tr(ROW_LABELS[label], lang) : label);

/* Convert an amount-like row value by replacing EUR amounts with target currency */
function convertRowValue(label, value, currencyObj) {
  if (!currencyObj || currencyObj.code === "EUR") return String(value);
  const { symbol, rate, code } = currencyObj;
  let s = String(value);

  // Replace "€123.45" → "$133.33"
  s = s.replace(/€\s*([\d.,]+)/g, (_, num) => {
    const n = parseFloat(num.replace(/\s/g, "").replace(",", ".")) * rate;
    return `${symbol}${n.toFixed(2)}`;
  });
  // Replace "123 EUR" / "123.45 EUR"
  s = s.replace(/([\d.,]+)\s*EUR\b/g, (_, num) => {
    const n = parseFloat(num.replace(/\s/g, "").replace(",", ".")) * rate;
    return `${n.toFixed(2)} ${code}`;
  });
  return s;
}

/* ─────────────  MAIN  ───────────── */
export async function generateReceiptPDF({ type, reference, rows, qrDataUrl, lang = "en", currency = "EUR" }) {
  const currencyObj = PDF_CURRENCIES.find((c) => c.code === currency) || PDF_CURRENCIES[0];
  const title = tr(T.titles[type] || {}, lang);
  const isInvoice = type === "ACQUIRING";
  const badge = isInvoice ? tr(T.badge_wait, lang) : tr(T.badge_ok, lang);

  // Build hidden HTML node
  const container = document.createElement("div");
  container.style.cssText = `
    position: fixed; left: -9999px; top: 0;
    width: 794px; padding: 0; background: #fff;
    font-family: -apple-system, "PingFang SC", "Microsoft YaHei", "Segoe UI", "Noto Sans", "Noto Sans SC", Arial, sans-serif;
    color: #0a0a0a; font-size: 14px; line-height: 1.55;
  `;

  const rowsHtml = rows.map(([l, v]) => `
    <tr>
      <td style="padding:12px 0;color:#666;border-bottom:1px solid #eee;font-size:13px">${trLabel(l, lang)}</td>
      <td style="padding:12px 0;text-align:right;font-weight:700;border-bottom:1px solid #eee;font-size:13px">${convertRowValue(l, v, currencyObj)}</td>
    </tr>
  `).join("");

  const qrHtml = qrDataUrl ? `
    <div style="text-align:center;margin:28px 0 10px">
      <img src="${qrDataUrl}" style="width:190px;height:190px;border:1px solid #eee;padding:8px;background:#fff" />
      <div style="margin-top:10px;color:#666;font-size:12px;font-style:italic">${tr(T.qr_hint, lang)}</div>
    </div>` : "";

  container.innerHTML = `
    <div style="background:#050505;padding:28px 40px;color:#fff">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <div style="font-size:24px;font-weight:900;letter-spacing:0.5px">TURBO <span style="color:#00F2FF">GLOBAL</span></div>
          <div style="color:#00F2FF;font-size:11px;margin-top:6px">${tr(T.subtitle, lang)}</div>
          <div style="color:#B8B8B8;font-size:11px;margin-top:2px">turbo.global</div>
        </div>
        <div style="text-align:right">
          <div style="color:#FFD700;font-weight:800;font-size:15px">${title}</div>
          <div style="color:#C8C8C8;font-size:10px;margin-top:6px">${tr(T.date, lang)}: ${new Date().toLocaleString(lang === "ru" ? "ru-RU" : lang === "zh" ? "zh-CN" : "en-GB")}</div>
          <div style="color:#C8C8C8;font-size:10px">${tr(T.ref, lang)}: ${reference}</div>
        </div>
      </div>
    </div>
    <div style="height:3px;background:#00F2FF"></div>

    <div style="padding:30px 40px">
      <div style="font-weight:700;font-size:14px;margin-bottom:12px">${tr(T.details, lang)}</div>
      <table style="width:100%;border-collapse:collapse">${rowsHtml}</table>
      ${qrHtml}
      <div style="margin-top:26px">
        <span style="display:inline-block;padding:8px 18px;border-radius:8px;background:${isInvoice ? "#FFD700" : "#10F4A7"};color:#050505;font-weight:800;font-size:12px;letter-spacing:0.5px">${badge}</span>
      </div>
    </div>

    <div style="padding:14px 40px 22px;border-top:1px solid #E5E5E5;color:#8A8A8A;font-size:10px;line-height:1.7">
      <div>${tr(T.footer_line1, lang)}</div>
      <div>${tr(T.footer_line2, lang)}</div>
      <div style="color:#00B8C8;margin-top:4px">${type} · ${reference}</div>
    </div>
  `;

  document.body.appendChild(container);
  try {
    const canvas = await html2canvas(container, { scale: 1.4, backgroundColor: "#FFFFFF", logging: false, useCORS: true });
    const imgData = canvas.toDataURL("image/jpeg", 0.85);

    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const imgH = (canvas.height * pageW) / canvas.width;
    pdf.addImage(imgData, "JPEG", 0, 0, pageW, imgH);
    pdf.save(`TGC_${type}_${reference}.pdf`);
  } finally {
    document.body.removeChild(container);
  }
}
